/**
 * Server-backed drop-in replacement for AssistantCloudLocal (same AssistantCloud API surface),
 * used by tenants with `persistence: 'server'`. Threads/messages live in bazak-mastra's memory
 * and are read over `/:tenant/threads*`; IndexedDB is NEVER opened — this class has no IDB
 * imports at all, which is what guarantees resumable tenants stay IDB-free.
 *
 * All endpoints require a `resourceId` query param (ownership is verified server-side; 404 on
 * mismatch).
 */
import {
  createAssistantStream,
  type AssistantStream,
  type AssistantStreamController,
} from 'assistant-stream';

import { AuthAPI } from '@/lib/assistant-cloud-local/auth-api';
import { FilesAPI } from '@/lib/assistant-cloud-local/files-api';
import type {
  MessageCreateInput,
  MessageCreateResponse,
  MessageData,
  MessageListOptions,
  MessageListResponse,
  MessageUpdateInput,
  ThreadCreateInput,
  ThreadCreateResponse,
  ThreadData,
  ThreadListResponse,
  ThreadUpdateInput,
} from '@/lib/assistant-cloud-local/types';
import { safeLocalStorage } from '@/lib/storage/safe-storage';
import { readRawPersistedThreadId } from '@/lib/sync/store';
import { safeRandomUUID } from '@/lib/utils/safe-uuid';
import { getOrCreateThreadUuid, getPendingLocalThreadId } from '@/lib/utils/thread';

const AI_SDK_V6_FORMAT = 'ai-sdk/v6';

const identityMarkerKey = (tenant: string) => `bazak-server-identity:${tenant}`;

/**
 * Called after the first successful chat POST: the HttpOnly identity cookie now exists but JS
 * can't read it, so this localStorage marker is the client-side signal that server reads are
 * authorized. Until it's set, the adapter answers reads locally with empty results instead of
 * hitting the API — a brand-new visitor must produce zero 401/403 network noise on page load.
 */
export function markServerIdentityEstablished({ tenant }: { tenant: string }): void {
  safeLocalStorage.setItem(identityMarkerKey(tenant), '1');
}

export function hasServerIdentity({ tenant }: { tenant: string }): boolean {
  if (safeLocalStorage.getItem(identityMarkerKey(tenant)) === '1') return true;
  // Rollout migration: browsers from before the marker existed hold a persisted server thread
  // id — clear evidence of an established identity cookie. Backfill the marker so restore can
  // read history (a truly cookieless browser just gets one graceful 403 → empty results).
  if (readRawPersistedThreadId()) {
    markServerIdentityEstablished({ tenant });
    return true;
  }
  return false;
}

export interface AssistantCloudServerOptions {
  /** The tenant's agent root, the chat route's own (see `chatAgentUrl`). */
  agentUrl: string;
  tenant: string;
  /** Resolves the durable resource id (the persisted-thread owner). */
  getResourceId: () => string;
}

interface ServerThread {
  id: string;
  title?: string;
  createdAt: string;
  updatedAt: string;
}

interface ServerUIMessage {
  id: string;
  role: string;
  parts?: unknown[];
  metadata?: unknown;
  createdAt?: string;
}

class ServerHttp {
  private identityMintAttempted = false;

  constructor(private readonly options: AssistantCloudServerOptions) {}

  /**
   * Self-healing for a missing/expired identity cookie (e.g. the minting chat response never
   * delivered its headers because the user reloaded instantly): mint via the dedicated identity
   * endpoint — the same claimed-resourceId → HMAC binding the chat POST performs — then let the
   * caller retry once. Attempted at most once per adapter instance.
   */
  private async mintIdentity(): Promise<boolean> {
    if (this.identityMintAttempted) return false;
    this.identityMintAttempted = true;
    try {
      const response = await fetch(this.url('/identity'), {
        method: 'POST',
        credentials: 'include',
      });
      return response.status === 204;
    } catch {
      return false;
    }
  }

  url(path: string): string {
    const { agentUrl, getResourceId } = this.options;
    const sep = path.includes('?') ? '&' : '?';
    return `${agentUrl}${path}${sep}resourceId=${encodeURIComponent(getResourceId())}`;
  }

  async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(this.url(path), {
      ...init,
      // The identity cookie (minted by the chat POST) authorizes these reads server-side.
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
    if (!response.ok) {
      throw new Error(
        `AssistantCloudServer: ${init?.method ?? 'GET'} ${path} → ${response.status}`,
      );
    }
    return (await response.json()) as T;
  }

  /**
   * GET variant for boot-time reads: before the first chat POST the browser has no identity
   * cookie yet, so the server answers 401/403 — that is "no data for you", not a failure.
   * Returning null lets callers render an empty state instead of breaking thread init.
   */
  async requestOrNull<T>(path: string): Promise<T | null> {
    if (!hasServerIdentity({ tenant: this.options.tenant })) {
      return null;
    }
    let response = await fetch(this.url(path), { credentials: 'include' });
    if ((response.status === 401 || response.status === 403) && (await this.mintIdentity())) {
      response = await fetch(this.url(path), { credentials: 'include' });
    }
    if (response.status === 401 || response.status === 403) return null;
    if (!response.ok) {
      throw new Error(`AssistantCloudServer: GET ${path} → ${response.status}`);
    }
    return (await response.json()) as T;
  }
}

function toThreadData(thread: ServerThread): ThreadData {
  const created = new Date(thread.createdAt);
  const updated = new Date(thread.updatedAt);
  return {
    id: thread.id,
    external_id: null,
    title: thread.title ?? '',
    is_archived: false,
    created_at: created,
    updated_at: updated,
    last_message_at: updated,
  };
}

function usableDate(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return isNaN(date.getTime()) ? undefined : date;
}

/**
 * Chains chronological UI messages into the cloud message shape (newest first, parent links).
 *
 * The list arrives in order, so each message's stamp is held at or after the one before it. Two
 * things make that necessary: a message can reach us with no `createdAt` at all, and a reply can
 * carry one that predates the message it answers — the assistant's is measured from when its
 * stream began, which is at or before the moment the question was stamped. Either way the thread
 * would render a reply above its own question, or dated 1970.
 */
export function toCloudMessages(
  messages: ServerUIMessage[],
  format: string = AI_SDK_V6_FORMAT,
): MessageData[] {
  const out: MessageData[] = [];
  let previous: Date | undefined;
  for (let i = 0; i < messages.length; i++) {
    const { id, createdAt, ...rest } = messages[i];
    // `createdAt` is an untyped string off the wire, and `new Date('nonsense')` is a truthy Invalid
    // Date. Read unparseable the same as absent, or its `NaN` becomes the floor for every message
    // after it and the whole batch comes back invalid.
    const stated = usableDate(createdAt);
    const floor = previous ? previous.getTime() + 1 : undefined;
    const at =
      stated && (floor === undefined || stated.getTime() >= floor)
        ? stated
        : new Date(floor ?? Date.now());
    previous = at;
    out.push({
      id,
      parent_id: i === 0 ? null : messages[i - 1].id,
      height: i + 1,
      format,
      content: rest as MessageData['content'],
      created_at: at,
      updated_at: at,
    });
  }
  return out.reverse();
}

class ServerMessagesAPI {
  constructor(private readonly http: ServerHttp) {}

  // The chat route persists messages into Mastra memory as the run streams; the runtime's
  // write-through is redundant in server mode, so writes are acknowledged without a request.
  async create(_threadId: string, data: MessageCreateInput): Promise<MessageCreateResponse> {
    return { message_id: data.id ?? safeRandomUUID() };
  }

  async update(_threadId: string, _messageId: string, _data: MessageUpdateInput): Promise<void> {}

  async list(threadId: string, options?: MessageListOptions): Promise<MessageListResponse> {
    const result = await this.http.requestOrNull<{ messages: ServerUIMessage[] }>(
      `/threads/${encodeURIComponent(threadId)}/messages`,
    );
    return {
      messages: toCloudMessages(result?.messages ?? [], options?.format ?? AI_SDK_V6_FORMAT),
    };
  }

  async get(_messageId: string): Promise<MessageData | null> {
    return null;
  }
}

class ServerThreadsAPI {
  public messages: ServerMessagesAPI;

  constructor(private readonly http: ServerHttp) {
    this.messages = new ServerMessagesAPI(http);
  }

  async list(): Promise<ThreadListResponse> {
    const result = await this.http.requestOrNull<{ threads: ServerThread[] }>('/threads');
    return { threads: (result?.threads ?? []).map(toThreadData) };
  }

  // The backend creates the thread when the first chat POST arrives with `memory.thread`; here
  // we only mint the durable id, honoring the pending-local-thread handshake the transport uses
  // so both sides agree on the UUID.
  async create(data: ThreadCreateInput): Promise<ThreadCreateResponse> {
    const pendingThreadId = getPendingLocalThreadId();
    const id =
      data.id ?? (pendingThreadId ? getOrCreateThreadUuid(pendingThreadId) : safeRandomUUID());
    return { thread_id: id };
  }

  async update(threadId: string, data: ThreadUpdateInput): Promise<void> {
    if (data.title === undefined) return;
    await this.http.request(`/threads/${encodeURIComponent(threadId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ title: data.title }),
    });
  }

  async delete(threadId: string): Promise<void> {
    await this.http.request(`/threads/${encodeURIComponent(threadId)}`, { method: 'DELETE' });
  }

  async get(threadId: string): Promise<ThreadData | null> {
    // Single-thread endpoint (NOT the list): a thread created moments ago may miss the list
    // snapshot, and aui's cloud adapter uses this exact call to ADOPT unlisted threads after a
    // reload. Throwing on not-found lets switchToThread reject cleanly into the restore retry.
    const result = await this.http.requestOrNull<{ thread: ServerThread }>(
      `/threads/${encodeURIComponent(threadId)}`,
    );
    if (!result?.thread) throw new Error(`AssistantCloudServer: thread ${threadId} not found`);
    return toThreadData(result.thread);
  }
}

class ServerRunsAPI {
  constructor(private readonly threads: ServerThreadsAPI) {}

  async stream(body: {
    thread_id: string;
    assistant_id: string;
    messages: readonly unknown[];
  }): Promise<AssistantStream> {
    if (body.assistant_id !== 'system/thread_title') {
      return createAssistantStream(async (controller: AssistantStreamController) =>
        controller.close(),
      );
    }
    // Server-persistence tenants own titling server-side. Stream ONLY the server's title; when
    // it hasn't been generated yet, stream nothing so the row keeps its default label — an
    // intermediate truncate here made the title visibly flicker (default → truncate → LLM
    // title). The title sync re-invokes this once the server title exists.
    const serverThread = await this.threads.get(body.thread_id).catch(() => null);
    const title = serverThread?.title;
    return createAssistantStream(async (controller: AssistantStreamController) => {
      if (title) controller.appendText(title);
      controller.close();
    });
  }

  async report(_data: unknown): Promise<void> {}
}

export class AssistantCloudServer {
  public threads: ServerThreadsAPI;
  public runs: ServerRunsAPI;
  public auth: AuthAPI;
  public files: FilesAPI;
  public telemetry = { enabled: false } as const;

  constructor(options: AssistantCloudServerOptions) {
    const http = new ServerHttp(options);
    this.threads = new ServerThreadsAPI(http);
    this.runs = new ServerRunsAPI(this.threads);
    this.auth = new AuthAPI();
    this.files = new FilesAPI();
  }
}
